import { useFormField } from '@cian/frontend-request-form-widget';
import { useCallback, useContext, useState, useEffect } from 'react';

import {
  EInputMode,
  IFieldChangeHandler,
  IInputModeChangeHandler,
  IUseAddressCadastreFieldProps,
  IUseAddressCadastreFieldResult,
  TAddressCadastreValue,
  TAddressValue,
  TCadastreValue,
} from '../types';
import { addressCadastreValidator } from './addressCadastreValidator';
import { DefaultInputModeContext } from '../context';
import { useAddressCadastreFieldTracking } from './useAddressCadastreFieldTracking';
import { isAddressCadastreSpecialFlowValidationErrorCode } from './isAddressCadastreSpecialFlowValidationErrorCode';

export function useAddressCadastreField({ name }: IUseAddressCadastreFieldProps): IUseAddressCadastreFieldResult {
  const { trackInputModeChange, trackSpecialFlowValidationError } = useAddressCadastreFieldTracking();
  const defaultInputMode = useContext(DefaultInputModeContext);
  const [inputMode, setInputMode] = useState<EInputMode>(defaultInputMode);
  const {
    field: { value },
    meta: { error, touched },
    helpers: { setValue, setTouched, setError },
  } = useFormField<TAddressCadastreValue>({
    name,
    validate: addressCadastreValidator,
  });

  // При изменении дефолтного режима ввода меняем выбранный режим ввода
  useEffect(() => {
    setInputMode(defaultInputMode);
  }, [defaultInputMode]);

  // Отслеживаем показ "специальных" ошибок
  useEffect(() => {
    if (!isAddressCadastreSpecialFlowValidationErrorCode(error)) {
      return;
    }
    trackSpecialFlowValidationError(error);
  }, [error, trackSpecialFlowValidationError]);

  // При переключении режима ввода сбрасываем ошибку
  const onInputModeChange = useCallback<IInputModeChangeHandler>(
    mode => {
      trackInputModeChange(mode);
      setError(undefined);
      setInputMode(mode);
    },
    [setInputMode, setError, trackInputModeChange],
  );

  const onAddressChange = useCallback<IFieldChangeHandler<TAddressValue>>(
    address => {
      setValue(address ? { address, cadastre: null } : null, true);
    },
    [setValue],
  );

  const onCadastreChange = useCallback<IFieldChangeHandler<TCadastreValue>>(
    cadastre => {
      setValue(cadastre ? { address: null, cadastre } : null, true);
    },
    [setValue],
  );

  const onBlur = useCallback<() => void>(() => {
    setTouched(true, true);
  }, [setTouched]);

  return {
    value,
    touched,
    error,
    inputMode,
    onBlur,
    onInputModeChange,
    onAddressChange,
    onCadastreChange,
  };
}
